import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useI18n } from '~Hooks';
import { gql, useQuery } from '@apollo/client';
import { GetTheme } from './__queries__';
import Header from './Header';

/** Duration of the order tile grow/shrink animation in ms. */
const ANIMATION_DURATION = 400;

const OuterWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  grid-template-rows: 175px 1fr;
  display: grid;
  background: var(--colorBlueGray100);
  position: relative;

  @media (orientation: portrait) {
    grid-template-rows: 104px 1fr;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: 1px;
`;

const ColumnLeft = styled.div`
  height: 100%;
`;

const ColumnRight = styled.div`
  height: 100%;
  background: var(--colorWhite);
`;

const ItemsLeft = styled.div`
  display: grid;
  grid-gap: 1px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 0px;
  overflow: hidden;
  height: 100%;

  @media (orientation: portrait) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(8, minmax(0, 1fr));
  }
`;

const ItemsRight = styled.div`
  display: grid;
  grid-gap: 1px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 0px;
  overflow: hidden;
  height: 100%;

  @media (orientation: portrait) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-template-rows: repeat(8, minmax(0, 1fr));
  }
`;

/**
 * Order tile. The `order-enter` / `order-exit` classes are added by the
 * surrounding `CSSTransition`: enter starts small, transparent and unthemed
 * then grows into its final color (so ready tiles "colour in" as they appear);
 * exit shrinks the tile while fading it out.
 */
const Order = styled.div<{ ready?: boolean; background?: string }>`
  position: relative;
  display: grid;
  align-items: center;
  justify-items: center;
  color: ${(props) => (props.ready ? 'var(--colorWhite)' : 'var(--colorBlueGray1200)')};
  background: ${(props) => (props.ready ? 'rgb(32, 153, 70)' : 'var(--colorWhite)')};
  transform-origin: center;
  transition: background ${ANIMATION_DURATION}ms ease, color ${ANIMATION_DURATION}ms ease,
    transform ${ANIMATION_DURATION}ms ease, opacity ${ANIMATION_DURATION}ms ease;

  &.order-enter {
    opacity: 0;
    transform: scale(0.3);
    background: var(--colorWhite);
    color: var(--colorBlueGray1200);
  }
  &.order-enter-active {
    opacity: 1;
    transform: scale(1);
    background: ${(props) => (props.ready ? props.background : 'var(--colorWhite)')};
    color: ${(props) => (props.ready ? 'var(--colorWhite)' : 'var(--colorBlueGray1200)')};
  }
  &.order-exit {
    opacity: 1;
    transform: scale(1);
  }
  &.order-exit-active {
    opacity: 0;
    transform: scale(0.3);
  }
`;

const OrderId = styled.div<{
  ready?: boolean;
}>`
  font-weight: 600;
  line-height: 88px;
  font-size: 80px;
`;

const PlusBadge = styled.div`
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 28px;
  font-weight: 600;
  color: var(--colorBlueGray600);
`;

const LabelWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  align-items: center;
  justify-items: center;
  background: var(--colorWhite);
  padding: 0 15%;
  box-sizing: border-box;

  @media (orientation: portrait) {
    padding: 0 5%;
  }
`;

const Label = styled.div`
  color: var(--colorBlueGray600);
  font-size: 48px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: center;
`;

const GET_THEME = gql`
  query GetTheme {
    operativeViewer {
      id
      onlineOrdering {
        id
        theme
        logoUrl
      }
    }
  }
`;

interface IOrder {
  id: string;
  easyId: string;
}

interface Props {
  cooking: IOrder[];
  ready: IOrder[];
  toggleMenu: () => void;
}

const ResponsiveCDS: FC<Props> = ({ cooking, ready, toggleMenu }) => {
  const { i18n } = useI18n();
  const [clickCount, setClickCount] = React.useState(0);
  const clickTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Number of cooking tiles mid-exit. Subtracted from the empty-cell filler
   * so the 4x4 cooking grid never temporarily overflows while a tile shrinks.
   */
  const [exitingCookingCount, setExitingCookingCount] = useState(0);

  const themeQuery = useQuery<GetTheme>(GET_THEME);
  const config = themeQuery?.data?.operativeViewer?.onlineOrdering;

  const numberLeftItems = 16;
  const numberRightItems = 8;

  const currentCooking = cooking.filter(
    (r, index, array) => array.findIndex((value) => value.easyId === r.easyId) === index,
  );

  const currentReady = ready
    .filter((r, index, array) => {
      const hasMultiple = currentCooking.findIndex((value) => value.easyId === r.easyId);
      const duplicate = array.findIndex((value) => value.easyId === r.easyId) === index;

      return hasMultiple == -1 || !duplicate;
    })
    .slice(0, numberRightItems);

  /**
   * Cap visible cooking orders to grid capacity (16) and surface any overflow
   * as a `+N` badge on the bottom-right cell. Keeping the grid at exactly 16
   * cells avoids the Chrome WebView 127 / Android 11 `grid-auto-rows` quirk
   * that produced a crammed 5th row on the LG commercial display.
   */
  const displayedCooking = currentCooking.slice(0, numberLeftItems);
  const hiddenCookingCount = Math.max(0, currentCooking.length - numberLeftItems);

  const cookingEmptyCount = Math.max(
    0,
    numberLeftItems - displayedCooking.length - exitingCookingCount,
  );

  /**
   * Delay swapping in the empty-state label until exit animations finish; if
   * we swap immediately when the column drops to zero, the last tile would
   * unmount mid-shrink and the animation would never play. Going the other
   * direction (hiding the label when a new order arrives) is always immediate.
   */
  const [delayedCookingEmpty, setDelayedCookingEmpty] = useState(() => cooking.length === 0);
  const [delayedReadyEmpty, setDelayedReadyEmpty] = useState(() => ready.length === 0);

  useEffect(() => {
    if (currentCooking.length > 0) {
      setDelayedCookingEmpty(false);
      return;
    }
    const timeout = setTimeout(() => setDelayedCookingEmpty(true), ANIMATION_DURATION);
    return () => clearTimeout(timeout);
  }, [currentCooking.length]);

  useEffect(() => {
    if (currentReady.length > 0) {
      setDelayedReadyEmpty(false);
      return;
    }
    const timeout = setTimeout(() => setDelayedReadyEmpty(true), ANIMATION_DURATION);
    return () => clearTimeout(timeout);
  }, [currentReady.length]);

  const showCookingLabel = currentCooking.length === 0 && delayedCookingEmpty;
  const showReadyLabel = currentReady.length === 0 && delayedReadyEmpty;

  // Handle clicks to detect 4 rapid clicks in order to navigate back to gateway - this to prevent accidental clicks
  const handleClick = () => {
    // Clear existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    const newCount = clickCount + 1;
    setClickCount(newCount);

    // If we've reached 4 clicks, toggle the menu
    if (newCount >= 4) {
      toggleMenu();
      setClickCount(0);
    } else {
      // Reset counter after 3 seconds of inactivity
      clickTimeoutRef.current = setTimeout(() => {
        setClickCount(0);
      }, 3000);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  return (
    <OuterWrapper>
      <Header
        background={config?.theme?.menuBackgroundColor}
        color={config?.theme?.menuTextColor}
        logoUrl={config?.logoUrl}
        preparingLabel={i18n('CustomerDisplay.Preparing')}
        readyLabel={i18n('CustomerDisplay.Ready')}
      />

      <Wrapper onClick={handleClick}>
        <ColumnLeft>
          {showCookingLabel ? (
            <LabelWrapper>
              <Label>{i18n('CustomerDisplay.KeepAnEyeHere')}</Label>
            </LabelWrapper>
          ) : (
            <ItemsLeft>
              <TransitionGroup component={null}>
                {displayedCooking.map((order, index) => {
                  const isLastVisibleCell = index === displayedCooking.length - 1;
                  const showOverflowBadge = isLastVisibleCell && hiddenCookingCount > 0;
                  return (
                    <CSSTransition
                      key={order.id}
                      timeout={ANIMATION_DURATION}
                      classNames="order"
                      onExit={() => setExitingCookingCount((c) => c + 1)}
                      onExited={() => setExitingCookingCount((c) => Math.max(0, c - 1))}>
                      <Order>
                        <OrderId>{order.easyId}</OrderId>
                        {showOverflowBadge && <PlusBadge>+{hiddenCookingCount}</PlusBadge>}
                      </Order>
                    </CSSTransition>
                  );
                })}
              </TransitionGroup>
              {Array.from({ length: cookingEmptyCount }, (_, i) => (
                <Order key={`empty-cooking-${i}`} />
              ))}
            </ItemsLeft>
          )}
        </ColumnLeft>
        <ColumnRight>
          {showReadyLabel ? (
            <LabelWrapper>
              <Label>{i18n('CustomerDisplay.ReadyMeals')}</Label>
            </LabelWrapper>
          ) : (
            <ItemsRight>
              <TransitionGroup component={null}>
                {currentReady.map((order) => (
                  <CSSTransition key={order.id} timeout={ANIMATION_DURATION} classNames="order">
                    <Order ready>
                      <OrderId>{order.easyId}</OrderId>
                    </Order>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </ItemsRight>
          )}
        </ColumnRight>
      </Wrapper>
    </OuterWrapper>
  );
};

export default React.memo(ResponsiveCDS);
